import React, { useEffect, useCallback, useState } from 'react';
import { Paper, Table, TableBody, TableContainer } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../app/store/configureStore';
import {
  fetchMenuDetailsAsync,
  updateMenuMealItemsAsync,
} from '../../store/menuSlice';
import { fetchMealItemListAsync } from '../../../mealItems/mealItemListSlice';
import LoadingComponent from '../../../../../app/layout/LoadingComponent';
import ListTitleBox from '../../../../../app/components/list/ListTitleBox';
import ErrorComponent from '../../../../../app/layout/ErrorComponent';
import { MenuDetails as MenuDetailsType } from '../../types';
import { SelectOption } from '../../../../../app/components/SelectOption';
import { toast } from 'react-toastify';
import i18n from '../../../../../i18n';
import { useConfirm } from '../../../../../app/hooks/useConfirm';
import { MenuAvailabilityRow } from './MenuAvailabilityRow';
import { MenuItemTable } from './MenuItemTable';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import MealItem from '../../../../../app/data/models/mealItem';

export const MenuDetails: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { restaurantId: restaurantIdString, menuId: menuIdString } = useParams<{
    restaurantId: string;
    menuId: string;
  }>();
  const restaurantId = restaurantIdString;
  const menuId = menuIdString;
  const { menuDetailsStatus } = useAppSelector((state) => state.menus);
  const { mealItems } = useAppSelector((state) => state.mealItems);
  const [menuDetails, setMenuDetails] = useState<MenuDetailsType | null>(null);
  const [mealItemSelectionList, setMealItemSelectionList] = useState<
    SelectOption[]
  >([]);
  const [selectedMealItems, setSelectedMealItems] = useState<MealItem[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [target] = useState('');
  const [saving, setSaving] = useState(false);
  const { confirm } = useConfirm();

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasChanges && currentLocation.pathname !== nextLocation.pathname
  );

  const fetchMenuDetails = useCallback(async () => {
    try {
      const result = await dispatch(
        fetchMenuDetailsAsync({ restaurantId, menuId })
      ).unwrap();
      setMenuDetails(result);
      setSelectedMealItems(result.mealItems);
    } catch {
      // Error handling is managed by the status
    }
  }, [dispatch, restaurantId, menuId]);

  useEffect(() => {
    fetchMenuDetails();
    dispatch(fetchMealItemListAsync(restaurantId));
  }, [fetchMenuDetails, dispatch, restaurantId]);

  useEffect(() => {
    if (!mealItems) return;
    const selectedMealItemIds = selectedMealItems.map((item) => item.id);
    const filteredItems = mealItems.filter(
      (item) => !selectedMealItemIds.includes(item.id)
    );
    setMealItemSelectionList(
      filteredItems.map((item) => ({ id: item.id, label: item.name }))
    );
  }, [mealItems, selectedMealItems]);

  const handleBack = useCallback(() => {
    navigate(`/manager/restaurants/${restaurantId}/menus`);
  }, [navigate, restaurantId]);

  const handleRemoveMealItem = useCallback(
    (id: string) => {
      const mealItem = selectedMealItems.find((item) => item.id === id);
      if (mealItem) {
        setSelectedMealItems(
          selectedMealItems.filter((item) => item.id !== id)
        );
        setHasChanges(true);
      }
    },
    [selectedMealItems]
  );

  const onAddItem = useCallback(
    (id: string) => {
      const mealItem = mealItems.find((item) => item.id === id);
      if (mealItem) {
        setSelectedMealItems([...selectedMealItems, mealItem]);
        setHasChanges(true);
      }
    },
    [mealItems, selectedMealItems]
  );

  const onSaveAction = async () => {
    setSaving(true);
    try {
      await dispatch(
        updateMenuMealItemsAsync({
          restaurantId,
          menuId,
          orderedMealItemIds: selectedMealItems.map((item) => item.id),
        })
      ).unwrap();
      toast.success(t('menu.details.update-meal-items.success'));
      setHasChanges(false);
    } catch {
      toast.error(t('menu.details.update-meal-items.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) return;

      const oldIndex = selectedMealItems.findIndex(
        (item) => item.id === active.id
      );
      const newIndex = selectedMealItems.findIndex(
        (item) => item.id === over.id
      );

      const reorderedItems = arrayMove(selectedMealItems, oldIndex, newIndex);

      setSelectedMealItems(reorderedItems);
      setHasChanges(true);
    },
    [selectedMealItems]
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      const handleBlock = async () => {
        const confirmed = await confirm({
          title: t('common.discard-changes.title'),
          message: t('common.discard-changes.message'),
          confirmText: t('common.discard-changes.confirm'),
          cancelText: t('common.discard-changes.cancel'),
        });

        if (confirmed) {
          blocker.proceed();
        } else {
          blocker.reset();
        }
      };
      handleBlock();
    }
  }, [blocker, confirm, t]);

  const header = (
    <ListTitleBox
      title={
        menuDetails?.title.languages.find(
          (language) => language.languageCode === i18n.language
        )?.value ||
        menuDetails?.title.languages.find(
          (language) => language.languageCode === 'en-US'
        )?.value
      }
      primaryButtonText={t('common.save')}
      onPrimaryClick={onSaveAction}
      primaryButtonDisabled={!hasChanges}
      primaryButtonProps={{
        loading: saving,
      }}
      onBackClick={handleBack}
    />
  );

  if (menuDetailsStatus === 'loading') {
    return <LoadingComponent header={header} />;
  }

  if (menuDetailsStatus === 'failed') {
    return (
      <ErrorComponent
        header={header}
        title={t('errors.generic.title')}
        description={t('errors.generic.description')}
        primaryButtonText={t('common.retry')}
        secondaryButtonText={t('common.back')}
        onPrimaryAction={fetchMenuDetails}
        onSecondaryAction={handleBack}
      />
    );
  }

  if (!menuDetails) {
    return null;
  }

  return (
    <>
      {header}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table>
          <TableBody>
            <MenuAvailabilityRow
              restaurantId={restaurantId}
              menuId={menuId}
              onClick={() =>
                navigate(
                  `/manager/restaurants/${restaurantId}/menus/${menuId}/menu-availability`
                )
              }
            />
          </TableBody>
        </Table>
      </TableContainer>

      <TableContainer component={Paper}>
        <MenuItemTable
          selectedMealItems={selectedMealItems}
          mealItemSelectionList={mealItemSelectionList}
          target={target}
          onDragEnd={handleDragEnd}
          onRemoveMealItem={handleRemoveMealItem}
          onAddItem={onAddItem}
        />
      </TableContainer>
    </>
  );
};
